import { BatchPrintPreferenceSchema } from "@shared/schemas/enums/batch-print.enum";
import type { WebStatus } from "@shared/services/pim/types";
import type { LabelPrintBatchFileType, LineItemFulfillmentStatus, Region, ServiceId } from "@shared/types";
import { CATALOGUE_REGION_IDS } from "@shared/types/admin-catalogue";
import { ADMIN_ATTRIBUTE_FIELDS, ADMIN_TARGET_WEB_STATUSES } from "@shared/types/admin-product-update";
import { z } from "zod";

/**
 * Typed event catalog.
 *
 * Implements the BCC Event Catalog agreed with Breville Analytics & Data
 * Science (September 2026). The keys are the strings that reach Mixpanel, so a
 * typo fails the build; the schema beside each key is the contract for that
 * event's payload.
 *
 * Naming: `BCC {Component Group} / {Action}`, Title Case, action past tense.
 * The BCC prefix is an agreed deviation from Breville's Precision Events
 * guide — these components are specific to the roaster portal and generic
 * groups like `Onboarding` would otherwise collide with consumer-site events.
 *
 * Payloads here are FLAT and typed. Nesting under
 * `precisionEventData.bcc.<subtree>` is Freshpaint plumbing and happens in the
 * transport layer — see ./precision-event-data.ts. Each entry declares its own
 * subtree so there is never a second mapping table to drift.
 *
 * Enum values are the domain's own strings, verbatim. Each list is declared
 * `satisfies readonly <DomainUnion>[]` against a type-only import, so renaming
 * or removing a member of a domain union fails this file's build rather than
 * silently rotting the catalog.
 *
 * `satisfies` alone does NOT catch *widening* — it checks that every listed
 * value belongs to the union, not that every union member is listed. Lists
 * that are meant to be complete therefore carry an explicit exhaustiveness
 * assertion as well; lists that are deliberately a subset are marked as such.
 *
 * These values are not free to rename: if Mixpanel needs different ones, that
 * is a transformation and goes to the analytics team.
 */

/**
 * Fails to compile if `TUnion` gains a member that `TListed` does not cover.
 * `satisfies` guards the other direction.
 */
type AssertExhaustive<TUnion, TListed extends TUnion> = [Exclude<TUnion, TListed>] extends [never] ? true : never;

/** Compile-time guard: `BCC [Group ]/ Action`. `events.test.ts` enforces Title Case. */
type AnalyticsEventNamePattern = `BCC ${string}/ ${string}`;

/** Counts are whole and non-negative. */
const count = z.number().int().nonnegative();

/**
 * `LabelPrintBatchFileType`, not `ShippingDocumentsTabType`.
 *
 * The two are easily confused and sit three lines apart in `labelPrint.ts`.
 * This one is the API value used by `getLabelPrintBatchShippingDocument` and
 * by the vendor's `batchPrintPreference`; `ShippingDocumentsTabType` is UI tab
 * state that never leaves the browser. An event describing a print uses this.
 */
const BATCH_DOCUMENT_TYPES = [
  "LabelsWithPackingSlips",
  "Label",
  "PackingSlip",
  "Manifest",
] as const satisfies readonly LabelPrintBatchFileType[];

// Complete by intent: a new printable document type must be added here, not
// silently rejected at runtime by the Zod enum.
const _allDocumentTypesCovered: AssertExhaustive<LabelPrintBatchFileType, (typeof BATCH_DOCUMENT_TYPES)[number]> = true;

/**
 * A deliberate SUBSET, so no exhaustiveness assertion: the failure half of
 * `LineItemFulfillmentStatus`. The other four states
 * (`CarrierScanned`, `Delivered`, `LabelCreated`, `WaitingToBeFulfilled`) are
 * progress, not failure, so they cannot appear on a label failure.
 */
const LABEL_FAILURE_STATUSES = [
  "AddressValidationError",
  "Canceled",
  "CarrierFulfillmentError",
  "DeliveryError",
  "Error",
  "LabelError",
  "ProductError",
  "ZenkraftAddressError",
] as const satisfies readonly LineItemFulfillmentStatus[];

/**
 * A deliberate SUBSET, so no exhaustiveness assertion: the reachable part of
 * `ServiceId`. The rest (`shipstation`, `pim`, `d365`,
 * `pbb`, `fg`, `fg-pbb`) are backend integrations a roaster never imports
 * products from.
 */
const PRODUCT_IMPORT_SOURCES = ["shopify", "woocommerce"] as const satisfies readonly ServiceId[];

/**
 * Onboarding is a linear wizard, so it is one event with a step property
 * rather than an event per step. Steps are the routes a roaster actually
 * passes through: `/onboarding`, `/onboarding/step-2` (map products), and
 * `/onboarding/success`.
 */
const ONBOARDING_STEP_NAMES = ["connect", "mapProducts", "success"] as const;

const ONBOARDING_STATUSES = ["started", "completed", "failed"] as const;

// Complete by intent: a status change can start from any PIM web status.
const WEB_STATUSES = [
  "Visible for Sale",
  "Visible but not for Sale",
  "Not Visible",
  "Out of Stock",
  "Temporarily Out of Stock",
] as const satisfies readonly WebStatus[];

const _allWebStatusesCovered: AssertExhaustive<WebStatus, (typeof WEB_STATUSES)[number]> = true;

export interface AnalyticsEventDefinition {
  /** Nests under `precisionEventData.bcc.<subtree>`. */
  readonly subtree: string;
  readonly schema: z.ZodType;
}

export const ANALYTICS_EVENTS = {
  // Fulfillment covers batch, label and shipment as one group rather than
  // separate Batch and Labels groups: `batchId` threads the whole sequence and
  // every question worth asking spans it, so splitting them would mean
  // rejoining them in every report.
  "BCC Fulfillment / Batch Created": {
    subtree: "fulfillmentBatch",
    schema: z.object({
      batchId: z.string(),
      purchaseOrderCount: count,
      totalUnits: count,
    }),
  },
  // `batchPrintPreference` rides here rather than on the user profile. It is an
  // account setting, and the account cannot be a Mixpanel group entity without
  // Mixpanel's paid Group Analytics add-on, so the only way to attribute a print
  // to the
  // preference in force at the time is to send it with the print. On the
  // profile it would be current state, overwritten on every change, and could
  // not answer "did they print something other than their default".
  //
  // Optional: a vendor with no preference set has none, and `documentType`
  // alone is still a complete event.
  "BCC Fulfillment / Batch Printed": {
    subtree: "fulfillmentBatch",
    schema: z.object({
      batchId: z.string(),
      documentType: z.enum(BATCH_DOCUMENT_TYPES),
      batchPrintPreference: z.enum(BatchPrintPreferenceSchema.options).optional(),
    }),
  },
  // Succeeded/failed stay symmetric so they sort together in the event list.
  "BCC Fulfillment / Label Generation Succeeded": {
    subtree: "labelGeneration",
    schema: z.object({
      batchId: z.string(),
      labelCount: count,
    }),
  },
  "BCC Fulfillment / Label Generation Failed": {
    subtree: "labelGeneration",
    schema: z.object({
      batchId: z.string(),
      errorCode: z.enum(LABEL_FAILURE_STATUSES),
    }),
  },
  "BCC Fulfillment / Shipment Confirmed": {
    subtree: "fulfillmentBatch",
    schema: z.object({
      batchId: z.string(),
      orderCount: count,
    }),
  },
  "BCC Onboarding / Flow Step": {
    subtree: "onboarding",
    schema: z.object({
      stepName: z.enum(ONBOARDING_STEP_NAMES),
      stepNumber: count,
      status: z.enum(ONBOARDING_STATUSES),
    }),
  },
  "BCC Product Import / Started": {
    subtree: "productImport",
    schema: z.object({
      source: z.enum(PRODUCT_IMPORT_SOURCES),
      productCount: count,
    }),
  },
  "BCC Product Import / Completed": {
    subtree: "productImport",
    schema: z.object({
      source: z.enum(PRODUCT_IMPORT_SOURCES),
      importedCount: count,
      failedCount: count,
    }),
  },
  // A Beanz Manager's edits from the admin catalogue, each raised in Salesforce as approval requests.
  // `catalogueRegion` is the storefront being edited, distinct from the root `region` (the user's account region).
  // Attribute updates fan out to every SKU of a product, so the counts say how many requests were created.
  "BCC Admin Catalogue / Attribute Update Submitted": {
    subtree: "adminCatalogue",
    schema: z.object({
      field: z.enum(ADMIN_ATTRIBUTE_FIELDS),
      catalogueRegion: z.enum(CATALOGUE_REGION_IDS),
      skuCount: count,
      createdCount: count,
      failedCount: count,
    }),
  },
  "BCC Admin Catalogue / Status Change Submitted": {
    subtree: "adminCatalogue",
    schema: z.object({
      catalogueRegion: z.enum(CATALOGUE_REGION_IDS),
      fromStatus: z.enum(WEB_STATUSES),
      toStatus: z.enum(ADMIN_TARGET_WEB_STATUSES),
    }),
  },
  // `route` is the matched route *pattern* (`/batches/:batchId/summary`),
  // never `location.pathname` — a resolved path carries record ids and gives
  // Mixpanel unbounded cardinality. `tab` is separate because BCC keeps tab
  // state in the query string, so `route` alone collapses distinct views.
  "BCC / Page Viewed": {
    subtree: "navigation",
    schema: z.object({
      route: z.string(),
      tab: z.string().optional(),
    }),
  },
} as const satisfies Record<AnalyticsEventNamePattern, AnalyticsEventDefinition>;

/** Every event the apps are allowed to track. */
export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;

/** The validated payload shape for a single event. */
export type AnalyticsEventProperties<TName extends AnalyticsEventName> = z.infer<(typeof ANALYTICS_EVENTS)[TName]["schema"]>;

/** Which `precisionEventData.bcc` subtree an event's properties belong under. */
export const resolveEventSubtree = (eventName: AnalyticsEventName): string => ANALYTICS_EVENTS[eventName].subtree;

/** The four regions. */
export const ANALYTICS_REGIONS = ["BUS", "BUK", "BDE", "BAU"] as const satisfies readonly Region[];

// Complete by intent: a fifth region must widen this list.
const _allRegionsCovered: AssertExhaustive<Region, (typeof ANALYTICS_REGIONS)[number]> = true;

/** Identical to `Region`, named for where it is used. */
export type AnalyticsRegion = (typeof ANALYTICS_REGIONS)[number];

/**
 * The stand-in for a root property the token did not carry.
 *
 * One spelling across `region`, `role` and `tenureBand`, so a report filtering
 * out incomplete sessions has a single value to exclude rather than three
 * different absences to reason about.
 */
export const ANALYTICS_UNKNOWN = "unknown";

/**
 * What `region` may be at the payload root: the four real regions, or the
 * explicit unknown.
 *
 * Kept separate from `ANALYTICS_REGIONS`, which stays pinned to the `Region`
 * union with its exhaustiveness check intact — `"unknown"` is not a region and
 * must never leak into anywhere that means one.
 */
export const ANALYTICS_REGION_VALUES = [...ANALYTICS_REGIONS, ANALYTICS_UNKNOWN] as const;

export type AnalyticsRegionValue = (typeof ANALYTICS_REGION_VALUES)[number];

/**
 * How long the roaster has been on the platform, banded.
 *
 * A band, never `createdAt` itself. Two reasons, and the second is the one
 * that decides it:
 *
 *   - A creation date on every event is a per-account fingerprint sitting in
 *     an analytics pipeline for no analytical gain.
 *   - The band is a *root* property, so it records what was true when the
 *     event fired. That is the whole point of carrying it on events rather
 *     than on the user profile: a profile property is current state and is
 *     overwritten, so it cannot answer "do roasters abandon more in their
 *     first 30 days" — by the time you ask, nobody is in their first 30 days.
 *
 * `"unknown"` is a real band, not a gap: vendor records predating `createdAt`,
 * and the window before the vendor resolves, both land here. Reports must
 * exclude it explicitly rather than mistake it for a tenure.
 */
export const ANALYTICS_TENURE_BANDS = ["0-30d", "31-90d", "91-365d", "365d+", "unknown"] as const;

export type AnalyticsTenureBand = (typeof ANALYTICS_TENURE_BANDS)[number];

/**
 * **The user profile holds nothing, deliberately — there is no traits
 * argument.**
 *
 * Everything that would have gone on it — `vendorKey`, `region`, `role`,
 * `batchPrintPreference` — is a root property on every event or an event
 * property instead. A profile property is *current state*: it is overwritten
 * in place, so a report run next quarter sees today's value stamped over every
 * event this user ever fired. Root properties record what was true when the
 * event fired, which is the question the funnel work actually asks.
 *
 * Keeping the profile empty is also the strongest form of the PII rule. There
 * is no `people.set` call to review, so there is no path by which a name, an
 * email or an address reaches a Mixpanel profile — `identify()` carries the
 * `VendorContactId` and nothing else.
 *
 * If a profile property is ever genuinely needed, it needs a reason that
 * survives "would a root property answer this better?", and it needs the PII
 * review that this absence currently makes unnecessary.
 */

/** The apps that send analytics. */
export const ANALYTICS_APPS = ["bcc", "client"] as const;

export type AnalyticsApp = (typeof ANALYTICS_APPS)[number];

/**
 * The only properties allowed at the payload root. Everything else nests.
 *
 * Merged per-track rather than registered as Mixpanel super properties:
 * `register` persists to localStorage, so a stale `environment` would outlive
 * the build that set it.
 *
 * `app` overlaps with the `BCC` event-name prefix, deliberately. The prefix is
 * for humans browsing the event list; the property is for filtering, applies
 * to root-level shared properties too, and stays correct if `client/` ever
 * emits events or BCC grows another surface.
 *
 * `isImpersonating` is required, not optional, because forgetting it is
 * indistinguishable from `false`. Admin impersonation calls
 * `fetchVendorDetailsAndSetVendor`, which replaces `currentVendor` in
 * `VendorProvider` outright — so every event after that point carries the
 * roaster's `vendorKey` and is otherwise indistinguishable from the roaster
 * working. Filter on it in every report.
 *
 * `vendorKey` is optional only because the vendor has not resolved in the
 * first moments after mount. Nothing consumes it as a Mixpanel group key
 * today; it is the upgrade path if Group Analytics is ever enabled, and costs
 * one string per event. Do not strip it as dead weight. `distinct_id` stays
 * the `VendorContactId` — setting it to `vendorKey` would collapse every user
 * of a roaster into one identity. Considered and rejected.
 *
 * `region`, `role` and `tenureBand` are always present, falling back to
 * `"unknown"` when the token does not carry them — `AccountRegion` is absent
 * for a Breville roaster admin who has not picked a roaster yet, and
 * `profileName` can be empty. An explicit `"unknown"` beats an absent key:
 * "we did not know" and "nobody sent it" are the same fact, and a property
 * that is sometimes missing forces every report to handle two shapes. Reports
 * exclude one value rather than reasoning about three different absences.
 *
 * `"unknown"` is therefore a member of `ANALYTICS_REGION_VALUES` but NOT of
 * `ANALYTICS_REGIONS`, which stays pinned to the `Region` union so the
 * placeholder cannot leak into anywhere that means a real region.
 *
 * `vendorKey` is the exception and stays optional — see below.
 *
 * **These are the account-attribution properties, and they are here rather
 * than on a Mixpanel group entity because Group Analytics is a paid add-on
 * that is not on the plan.** `vendorKey`, `region`, `role` and `tenureBand`
 * describe the account on every event. That is more bytes than a group key,
 * and it is also strictly more useful: a group entity holds current state,
 * while these record the state at the moment the event fired.
 *
 * Names are not free to change. An overlapping-but-different name for a
 * property Breville already has at root level costs a Freshpaint SQL
 * transformation to reconcile, so check the shared-property list before
 * adding one here.
 */
export const ANALYTICS_DEFAULT_PROPERTIES_SCHEMA = z.object({
  app: z.enum(ANALYTICS_APPS),
  environment: z.string(),
  isImpersonating: z.boolean(),
  vendorKey: z.string().optional(),
  region: z.enum(ANALYTICS_REGION_VALUES),
  role: z.string(),
  tenureBand: z.enum(ANALYTICS_TENURE_BANDS),
});

export type AnalyticsDefaultProperties = z.infer<typeof ANALYTICS_DEFAULT_PROPERTIES_SCHEMA>;
